import { Suspense } from "react"
import { makeStyles } from "tss-react/mui"
import { Tooltip, Typography } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import {
    MeetingServiceOrderStatus,
    TimePeriodMeetingService,
} from "../../production/meetingService/meetingServiceTypes"
import { DashboardCard } from "../../../../dashboards/components/DashboardCard"
import { MERCHANT_PRODUCTION_MEETING_SERVICE_ROUTE } from "../../../../../constants/routes"
import { MerchantHomeQuery, fetchDashboardNotifications, fetchMeetingServiceOrderSummary } from "../merchantHomeApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { DashboardCardSkeleton } from "../../../../dashboards/components/DashboardCardSkeleton"
import { OrderStatusType } from "../merchantHomeTypes"
import { Box } from "@mui/system"

const useStyles = makeStyles()((theme) => ({
    title: {
        fontWeight: 500,
        color: theme.palette.tailwind.slate[700],
    },
    titleRed: {
        color: theme.palette.tailwind.red[500],
    },
}))

type OrderSummaryCardsType = Readonly<{
    merchantId: number
    handleClick: (tabId?: number) => void
}>

export function OrderSummaryPeriod({ merchantId, handleClick }: OrderSummaryCardsType) {
    const { classes } = useStyles()

    const meetingServiceTimePeriodResult = useApiQuery(fetchMeetingServiceOrderSummary, {
        queryName: MerchantHomeQuery.MEETING_SERVICE_ORDER_SUMMARY_TIME_PERIOD,
        dependencies: {
            merchantId,
            params: { period: TimePeriodMeetingService.TODAY.toUpperCase() },
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    return (
        <LocalizedStrict id="merchant-home-statistics-meeting-orders-card-tooltip" attrs={{ title: true }}>
            <Tooltip title="Number of meeting service orders today">
                <Box>
                    <LocalizedStrict id="merchant-home-meeting-service-number-of-orders-card" attrs={{ title: true }}>
                        <DashboardCard title="Number of orders" onClick={() => handleClick()}>
                            <Typography variant="h3" className={classes.title}>
                                {meetingServiceTimePeriodResult.data?.posSales?.[0]?.salesSummary?.numberOfOrders || 0}
                            </Typography>
                        </DashboardCard>
                    </LocalizedStrict>
                </Box>
            </Tooltip>
        </LocalizedStrict>
    )
}
export function OrderSummaryUnapprovedOrders({ merchantId, handleClick }: OrderSummaryCardsType) {
    const { classes, cx } = useStyles()

    const notificationsCountResponse = useApiQuery(fetchDashboardNotifications, {
        queryName: MerchantHomeQuery.MEETING_SERVICE_ORDER_SUMMARY_STATUS,
        dependencies: {
            merchantId,
            params: { status: OrderStatusType.READY_FOR_INVOICE.toUpperCase() },
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    const amount =
        notificationsCountResponse.data?.notifications?.find((n) => n.type === MeetingServiceOrderStatus.NOT_APPROVED)
            ?.numberOfOrders ?? 0

    return (
        <LocalizedStrict id="merchant-home-statistics-meeting-orders-approval-card-tooltip" attrs={{ title: true }}>
            <Tooltip title="Total number of meeting service orders awaiting approval">
                <Box>
                    <LocalizedStrict id="merchant-home-meeting-service-awaiting-approval-card" attrs={{ title: true }}>
                        <DashboardCard title="Awaiting approval" onClick={() => handleClick(1)}>
                            <Typography variant="h3" className={cx(classes.title, classes.titleRed)}>
                                {amount || 0}
                            </Typography>
                        </DashboardCard>
                    </LocalizedStrict>
                </Box>
            </Tooltip>
        </LocalizedStrict>
    )
}
// const OVERDUE_DAYS = 10

export function OrderSummaryReadyForInvoicing({ merchantId, handleClick }: OrderSummaryCardsType) {
    const { classes } = useStyles()

    const notificationsCountResponse = useApiQuery(fetchDashboardNotifications, {
        queryName: MerchantHomeQuery.MEETING_SERVICE_ORDER_SUMMARY_STATUS,
        dependencies: {
            merchantId,
            params: { status: OrderStatusType.READY_FOR_INVOICE.toUpperCase() },
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    const amount =
        notificationsCountResponse.data?.notifications?.find((n) => n.type === MeetingServiceOrderStatus.TO_BE_BOOKED)
            ?.numberOfOrders ?? 0

    return (
        <LocalizedStrict id="merchant-home-statistics-meeting-orders-invoicing-card-tooltip" attrs={{ title: true }}>
            <Tooltip title="Total number of meeting service orders awaiting booking and was delivered more than [X] days">
                <Box>
                    <LocalizedStrict
                        id="merchant-home-meeting-service-ready-for-invoicing-card"
                        attrs={{ title: true }}
                    >
                        <DashboardCard title="Ready for invoicing" onClick={() => handleClick(2)}>
                            <Typography variant="h3" className={classes.title}>
                                {amount || 0}
                            </Typography>
                            {/* <LocalizedStrict
                                id="merchant-home-meeting-service-ready-for-invoicing-card-description"
                                vars={{ overdueDays: OVERDUE_DAYS }}
                            >
                                <Typography variant="caption">&gt; {OVERDUE_DAYS} days ago</Typography>
                            </LocalizedStrict> */}
                        </DashboardCard>
                    </LocalizedStrict>
                </Box>
            </Tooltip>
        </LocalizedStrict>
    )
}

export function OrderSummaryCards() {
    const { merchantId } = useMerchant()
    const navigate = useNavigate()

    function handleClick(tabId: number = 0) {
        const baseUrl = `${MERCHANT_PRODUCTION_MEETING_SERVICE_ROUTE}?tab=${tabId}`

        navigate(baseUrl)
        window.scrollTo(0, 0)
    }

    return (
        <QueryErrorBoundary>
            <Suspense fallback={<DashboardCardSkeleton />}>
                <OrderSummaryPeriod merchantId={merchantId} handleClick={handleClick} />
            </Suspense>
            <Suspense fallback={<DashboardCardSkeleton />}>
                <OrderSummaryUnapprovedOrders merchantId={merchantId} handleClick={handleClick} />
            </Suspense>
            <Suspense fallback={<DashboardCardSkeleton />}>
                <OrderSummaryReadyForInvoicing merchantId={merchantId} handleClick={handleClick} />
            </Suspense>
        </QueryErrorBoundary>
    )
}
